import {ContentType, request} from './request'
import {LoginResponse} from '../responses/login'
import {
    ContactsUser,
    CurrentUser,
    CurrentUserUpdateData,
    Permission,
    PermissionGroup,
    Role,
    User,
    UserWithDepartments
} from '../responses/users'
import {
    DeleteResponse,
    EmptyResponse,
    FailedResponse,
    ListResponse,
    Query,
    RowResponse,
    SuccessResponse
} from '../responses/response'
import {Config, RefIntersection, RefKey} from '../responses/refs/refs'
import {
    FullProject,
    ProjectCloseData,
    ProjectListQuery,
    ProjectUpdateData,
    RelatedProject,
    ReservableResponse,
    ServerProject,
    TipKey
} from '../responses/projects'
import {ServerTask, TaskListQuery, UpdateTaskRequest} from '../responses/tasks'
import {FileCheckData, FileData} from '../responses/files'
import {Bool, ID, UpdateData} from '../types/UtilityTypes'
import {HistoryQuery, HistoryResponse, ReadResponseData} from '../responses/history'
import {TaskStatus} from '../constants/enums'
import {NotificationFullResponse, NotificationKind} from '../responses/notification'
import {NotificationMethod, NotificationSettingsRow} from '../responses/profile'
import {
    FullObject,
    ObjectCloseData,
    ObjectCreateData,
    ObjectUpdateRequest,
    ProjectWorkObject
} from '../responses/objects'
import {Comment, CommentRequestData} from '../responses/comments'
import {Tag} from '../responses/tags'
import {SearchResult} from '../responses/search'
import {
    ByClientData,
    ByManagerData,
    ByProjectData,
    ByObjectData,
    ByObjectQuery,
    ByUserData,
    ByUserQuery,
    StatisticsInWork,
    StatisticsResponse, ByTaskData, ByClientQuery
} from '../responses/statistics'
import {DepartmentTasksFilters} from '../redux/pages/task-list/TaskFilters'

export const apiRequests = {
    users: {
        login: request<{
            response: LoginResponse,
            data: { login: string, password: string }
        }>({method: 'post', url: '/api/login'}),
        logout: request<{ response: EmptyResponse }>({method: 'post', url: '/api/logout'}),
        getUserInfo: request<{ response: RowResponse<CurrentUser> }>({method: 'post', url: '/api/user/current/get'}),
        currentUpdate: request<{
            response: RowResponse<CurrentUser>,
            data: CurrentUserUpdateData,
        }>({method: 'post', url: '/api/user/current/update'}),
        tasks: request<{
            response: ListResponse<ServerTask>,
            params: { id: ID },
        }>({method: 'post', url: '/api/user/:id/tasks'}),
        refreshToken: request<{
            response: LoginResponse,
        }>({method: 'post', url: '/api/refresh-token'}),
        list: request<{
            response: ListResponse<UserWithDepartments>,
            query: Query & {
                all?: Bool,
                can_curated?: Bool,
                department?: ID,
            },
        }>({method: 'post', url: '/api/user/list'}),
        update: request<{
            data: Partial<UserWithDepartments>,
            params: { id: ID },
            response: RowResponse<UserWithDepartments>,
        }>({method: 'post', url: '/api/user/:id/update'}),
        notificationSettings: request<{
            response: ListResponse<NotificationSettingsRow>,
            params: { id: ID },
        }>({method: 'post', url: '/api/user/:id/notification/settings/list'}),
        notificationMethods: request<{
            response: ListResponse<NotificationMethod>,
            params: { userId: ID },
        }>({method: 'post', url: '/api/user/:userId/notification/methods/list'}),
        contacts: request<{
            response: ListResponse<ContactsUser>,
            query: Query & {
                all?: Bool,
                can_curated?: Bool,
                department?: ID,
            },
        }>({method: 'post', url: '/api/user/contacts'}),
    },
    dictionary: {
        add: request<{
            response: RowResponse<RefIntersection>,
            data: UpdateData<Partial<RefIntersection>>,
            params: { key: RefKey },
        }>({method: 'post', url: '/api/dictionary/:key/add'}),
        list: request<{
            response: ListResponse<RefIntersection>,
            params: { key: RefKey },
            query?: Query & DepartmentTasksFilters & {
                id?: ID,
                // for departments
                use_permissions?: boolean,
                with_users?: boolean,
                has_tasks?: boolean,
                // for work-object
                category?: ID,
                // for clients, departments
                all?: boolean,
            }
        }>({method: 'post', url: '/api/dictionary/:key/list'}),
        update: request<{
            response: RowResponse<RefIntersection>,
            data: UpdateData<Partial<RefIntersection>>,
            params: { key: RefKey, id: ID }
        }>({method: 'post', url: '/api/dictionary/:key/:id/update'}),
        delete: request<{
            response: DeleteResponse,
            params: { key: RefKey, id: ID }
        }>({method: 'post', url: '/api/dictionary/:key/:id/delete'}),
        configList: request<{
            response: ListResponse<Config>
        }>({method: 'post', url: '/api/dictionary/config/list'}),
        configUpdate: request<{
            data: Config,
            params: { key: string },
            response: RowResponse<Config>,
        }>({method: 'post', url: '/api/dictionary/config/:key/update'}),
        departmentList:  request<{
            response: ListResponse<RefIntersection>,
            params?: { key: RefKey },
            query?: Query
        }>({method: 'post', url: '/api/dictionary/department/list'}),
        directionList: request<{
            response: ListResponse<RefIntersection>,
            query?:Query
        }>({method:'post', url: '/api/dictionary/direction/list'})
    },
    project: {
        relations: request<{
            response: ListResponse<RelatedProject>,
            params: { projectId: ID },
            query: { is_design: Bool }
        }>({method: 'post', url: '/api/project/:projectId/relations'}),
        createFinplan: request<{
            response: RowResponse<ServerProject>,
            params: { projectId: ID },
        }>({method: 'post', url: '/api/project/:projectId/create-finplan'}),
        relationList: request<{
            response: ListResponse<RelatedProject>,
            params: { projectId: ID },
            query: Query,
        }>({method: 'post', url: '/api/project/:projectId/list-for-relate'}),
        list: request<{
            response: ListResponse<ServerProject>,
            query: ProjectListQuery,
        }>({method: 'post', url: '/api/project/list'}),
        get: request<{
            response: RowResponse<FullProject>,
            params: { projectId: ID },
            query: { with: 'full_tasks_structured' },
        }>({method: 'post', url: '/api/project/:projectId/get'}),
        add: request<{
            data: UpdateData<FullProject>
            response: RowResponse<ServerProject>
        }>({method: 'post', url: '/api/project/add'}),
        update: request<{
            data: ProjectUpdateData,
            response: RowResponse<ServerProject>,
            params: { projectId: ID }
        }>({method: 'post', url: '/api/project/:projectId/update'}),
        delete: request<{
            response: DeleteResponse,
            params: { projectId: ID },
        }>({method: 'post', url: '/api/project/:projectId/delete'}),
        cancel: request<{
            response: RowResponse<ServerProject>,
            params: { projectId: ID },
        }>({method: 'post', url: '/api/project/:projectId/cancel'}),
        close: request<{
            params: { projectId: ID },
            data: ProjectCloseData,
            response: RowResponse<FullProject>
        }>({method: 'post', url: '/api/project/:projectId/close'}),
        tip: request<{
            response: ListResponse<RefIntersection>,
            params: { key: TipKey },
            query: Query & {
                query?: string,
            },
        }>({method: 'post', url: '/api/project/tip/:key'}),
        tipWorkObject: request<{
            response: ListResponse<RefIntersection>,
            query: Query & {
                query?: string,
            },
        }>({method: 'post', url: '/api/project/tip/work_object'}),
        tipTaskType: request<{
            response: ListResponse<RefIntersection>,
            query: Query & {
                query?: string,
            },
        }>({method: 'post', url: ' /api/project/tip/tasks_type'}),
        targetUsers: request<{
            response: ListResponse<User>,
            params: { projectId: ID },
            query: Query,
        }>({method: 'post', url: '/api/project/:projectId/user/list'}),
        targetUsersEditTask: request<{
            response: ListResponse<User>,
            params: { projectId: ID, taskId: ID },
            query: Query,
        }>({method: 'post', url: '/api/task/:taskId/user/list'}),
        targetUsersNewTask: request<{
            response: ListResponse<User>,
            params: { projectId: ID }
            query: Query & {
                task_type?: ID,
            },
        }>({ method: 'post', url: '/api/project/:projectId/user/list'}),
        tasks: {
            list: request<{
                response: ListResponse<ServerTask>,
                params: { objectId: ID },
                query: { parents?: boolean, } & TaskListQuery,
            }>({method: 'post', url: '/api/project/work-object/:objectId/task/list'}),
            add: request<{
                response: RowResponse<ServerTask>,
                data: UpdateTaskRequest,
                params: { objectId: ID }
            }>({
                method: 'post',
                url: '/api/project/work-object/:objectId/task/add'
            }),
        },
        tags: {
            list: request<{
                response: ListResponse<Tag>,
                params: { objectId: ID },
            }>({method: 'post', url: '/api/project/work-object/:objectId/tag/list'}),
            add: request<{
                response: RowResponse<Tag>,
                data: { name: string },
                params: { objectId: ID }
            }>({method: 'post', url: '/api/project/work-object/:objectId/tag/add'}),
            delete: request<{
                response: DeleteResponse,
                params: { objectId: ID, tagId: ID }
            }>({method: 'post', url: '/api/project/work-object/:objectId/tag/:tagId/delete'}),
        },
        comment: request<{
            response: RowResponse<Comment>,
            data: CommentRequestData,
            params: { projectId: ID },
        }>({method: 'post', url: '/api/project/:projectId/comment'}),
        subscribe: request<{
            response: EmptyResponse,
            data: {
                users: ID[]
            },
            params: { projectId: ID },
        }>({method: 'post', url: '/api/project/:projectId/subscribe'}),
        unsubscribe: request<{
            response: EmptyResponse,
            params: { projectId: ID },
            data: {
                users: ID[]
            },
        }>({method: 'post', url: '/api/project/:projectId/unsubscribe'}),
        checkSource: request<{
            response: EmptyResponse
            params: { projectId: ID }
        }>({method: 'post', url: '/api/project/:projectId/check-source'}),
        reservable: request<{
            response: FailedResponse | ReservableResponse,
            query: {
                offset: number,
                list_size: number,
            }
        }>({method: 'post', url: '/api/project/list/reservable'}),
        reserve: request<{
            data: { id: ID[] }
        }>({method: 'post', url: '/api/project/list/reserve'}),
        archive: request<{
            response: ListResponse<ServerProject>,
            data: {
                id: ID[],
            }
        }>({method: 'post', url: '/api/project/list/archive'}),
        unarchive: request<{
            response: ListResponse<ServerProject>,
            data: {
                id: ID[],
            }
        }>({method: 'post', url: '/api/project/list/unarchive'})
    },
    workObject: {
        add: request<{
            data: Omit<ObjectCreateData, 'projectId'>,
            response: RowResponse<ProjectWorkObject>,
        }>({method: 'post', url: '/api/project/work-object/add'}),
        update: request<{
            data: ObjectUpdateRequest,
            response: RowResponse<ProjectWorkObject>,
            params: { objectId: ID },
        }>({method: 'post', url: '/api/project/work-object/:objectId/update'}),
        close: request<{
            data: ObjectCloseData,
            response: RowResponse<FullObject>,
            params: { objectId: ID },
        }>({method: 'post', url: '/api/project/work-object/:objectId/close'}),
        comment: request<{
            response: RowResponse<Comment>,
            data: CommentRequestData,
            params: { objectId: ID }
        }>({method: 'post', url: '/api/project/work-object/:objectId/comment'}),
    },
    tag: {
        list: request<{
            response: ListResponse<Tag>,
            query: Query,
        }>({method: 'post', url: '/api/tag/list'}),
        add: request<{
            response: RowResponse<Tag>,
            data: { name: string },
        }>({method: 'post', url: '/api/tag/add'}),
    },
    task: {
        get: request<{
            response: RowResponse<Required<ServerTask>>,
            params: { taskId: ID },
            query: { with_branch?: Bool }
        }>({method: 'post', url: '/api/task/:taskId/get'}),
        list: request<{
            response: ListResponse<ServerTask>,
            query?: { tasks_status?: TaskStatus },
        }>({method: 'post', url: '/api/task/list'}),
        update: request<{
            response: RowResponse<ServerTask>,
            data: Partial<UpdateTaskRequest>,
            params: { taskId: ID }
        }>({method: 'post', url: '/api/task/:taskId/update'}),
        delete: request<{
            response: DeleteResponse,
            params: { taskId: ID }
        }>({method: 'post', url: '/api/task/:taskId/delete'}),
        comment: request<{
            response: RowResponse<Comment>,
            data: CommentRequestData,
            params: { taskId: ID }
        }>({method: 'post', url: '/api/task/:taskId/comment'}),
        comments: request<{
            response: ListResponse<Comment>
            query: { resulted: boolean },
            params: { taskId: ID },
        }>({method: 'post', url: '/api/task/:taskId/comments'}),
        userDelete: request<{
            response: DeleteResponse,
            params: { taskId: ID, userId: ID },
        }>({method: 'post', url: '/api/task/:taskId/user/:userId/delete'}),
        waybill: request<{
            response: any,// eslint-disable-line
            params: {id: any},// eslint-disable-line
        }>({method: 'post', url:'/api/task/:id/waybill'}),
    },
    file: {
        uploadCheck: request<{
            response: EmptyResponse,
            data: FileCheckData
        }>({method: 'post', url: '/api/file/upload-check'}),
        upload: request<{
            response: ListResponse<FileData>,
            data: FormData
        }>({method: 'post', url: '/api/file/upload', type: ContentType.MULTIPART}),
        downloadGroup: request<{
            response: FailedResponse | (SuccessResponse & {
                link: string,
            }),
            data: {
                files: ID[],
                name?: string,
            }
        }>({method: 'post', url: '/api/file/group'}),
    },
    role: {
        list: request<{
            response: ListResponse<Role>,
            query: Query & { is_design?: Bool },
        }>({method: 'post', url: '/api/role/list'}),
        update: request<{
            data: { permissions: ID[], is_design: Bool, is_finplan: Bool },
            params: { roleId: ID },
            response: RowResponse<Role>,
        }>({method: 'post', url: '/api/role/:roleId/update'})
    },
    permission: {
        list: request<{
            response: ListResponse<Permission>,
        }>({method: 'post', url: '/api/permission/list'}),
        listByGroup: request<{
            response: ListResponse<PermissionGroup>,
        }>({method: 'post', url: '/api/permission/list?by_group=true'}),
    },
    comment: {
        get: request<{
            response: RowResponse<Comment>,
            params: { commentId: ID },
        }>({method: 'post', url: '/api/comment/:id/get'}),
        update: request<{
            data: CommentRequestData,
            response: RowResponse<Comment>,
            params: { id: ID },
        }>({method: 'post', url: '/api/comment/:id/update'}),
        delete: request<{
            response: DeleteResponse,
            params: { id: ID },
        }>({method: 'post', url: '/api/comment/:id/delete'}),
    },
    history: {
        read: request<{
            response: RowResponse<ReadResponseData>,
            params: { messageId: ID },
        }>({method: 'post', url: '/api/history/:messageId/read'}),
        project: {
            list: request<{
                response: HistoryResponse,
                params: { projectId: ID },
                query: HistoryQuery,
            }>({method: 'post', url: '/api/project/:projectId/history'}),
            readAll: request<{
                params: { projectId: ID },
                response: EmptyResponse,
            }>({method: 'post', url: '/api/project/:projectId/history/read'})
        },
        object: {
            list: request<{
                response: HistoryResponse,
                params: { objectId: ID },
                query: HistoryQuery,
            }>({method: 'post', url: '/api/project/work-object/:objectId/history'}),
            readAll: request<{
                params: { objectId: ID },
                response: EmptyResponse,
            }>({method: 'post', url: '/api/work-object/:objectId/history/read'})
        },
        task: {
            list: request<{
                response: HistoryResponse,
                params: { taskId: ID },
                query?: HistoryQuery,
            }>({method: 'post', url: '/api/task/:taskId/history'}),
            readAll: request<{
                params: { taskId: ID },
                response: EmptyResponse,
            }>({method: 'post', url: '/api/task/:taskId/history/read'}),
        }
    },
    notification: {
        push: request<{
            data: PushSubscription,
            response: EmptyResponse,
        }>({method: 'post', url: '/api/push'}),
        list: request<{
            response: NotificationFullResponse,
            query: Query & {read?: Bool, kind?: NotificationKind }
        }>({method: 'post', url: '/api/user/current/notification/list'}),
        action: request<{
            response: NotificationFullResponse,
            query: Query & {read?: Bool, kind?: NotificationKind }
        }>({method: 'post', url: '/api/user/current/action/list'}),
        read: request<{
            params: { id: ID },
            response: EmptyResponse,
        }>({method: 'post', url: '/api/notification/:id/read'}),
        readAll: request<{
            response: EmptyResponse,
        }>({method: 'post', url: '/api/user/current/notification/read'}),
    },
    search: {
        list: request<{
            response: ListResponse<SearchResult>,
            query: ProjectListQuery,
        }>({method: 'post', url: '/api/search/list'}),
    },
    statistics: {
        inWork: request<{
            response: StatisticsResponse<StatisticsInWork>
        }>({method: 'post', url: '/api/statistics/in-work'}),
        byClient: request<{
            response: StatisticsResponse<ByClientData>,
            query: ByClientQuery,
        }>({method: 'post', url:'/api/statistics/by-client'}),
        byManager: request<{
            response: StatisticsResponse<ByManagerData>,
            query: ByUserQuery,
        }>({method: 'post', url: '/api/statistics/by-manager'}),
        byProject: request<{
            response: StatisticsResponse<ByProjectData>,
            query: ByUserQuery,
        }>({method: 'post', url: '/api/statistics/by-project'}),
        byTask: request<{
            response: StatisticsResponse<ByTaskData>,
            query: ByUserQuery,
        }>({method: 'post', url: '/api/statistics/by-task'}),
        byUser: request<{
            response: StatisticsResponse<ByUserData>,
            query: ByUserQuery,
        }>({method: 'post', url: '/api/statistics/by-user'}),
        byObject: request<{
            response: StatisticsResponse<ByObjectData>,
            query: ByObjectQuery,
        }>({method: 'post', url: '/api/statistics/by-object'}),
    }
}
